import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import OrientationTour from "@/components/OrientationTour";
import KadanaCopilot from "@/components/KadanaCopilot";
import Greeting from "@/components/Greeting";
import PresenceBar from "@/components/PresenceBar";
import DataTable from "@/components/DataTable";
import ReorderHandle from "@/components/ReorderHandle";
import SavedIndicator from "@/components/SavedIndicator";
import RelTime from "@/components/RelTime";
import { fmt } from "@/components/states";
import { HelpTip } from "@/components/kit";
import { useConfirm } from "@/context/ConfirmContext";
import { useDemo, DEMO } from "@/lib/demo";
import { useWidgetOrder } from "@/hooks/useWidgetOrder";
import { optimistic } from "@/lib/optimistic";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, FlaskConical, RotateCcw, ShieldAlert } from "lucide-react";

const ADMIN_TOUR = [
  { title: "Welcome to the Super Admin Console", body: "This is your control room. There are 10 core menus plus two governance surfaces added recently — this quick tour shows what's where." },
  { title: "Command Center", body: "Start here each shift: one pane of glass with live tiles for margin, verification backlog, fleet health and alerts, each drilling into its full menu." },
  { title: "Menu 11 — Catalogue & Data Governance", body: "The connector Verification Queue lives here (review access before a region is marketed as verified), alongside the region taxonomy, sector map and excluded-entity registry." },
  { title: "You're set", body: "Press ? anywhere for keyboard shortcuts, and Cmd/Ctrl+K to jump to any screen. Reorder these cards with the arrows to put what matters first. This tour only shows once." },
];

// Status is never conveyed by colour alone (design rule): each tag pairs an icon with its label.
const STATUS_ICON = { ok: CheckCircle2, warn: AlertTriangle, bad: XCircle };
function StatusTag({ kind = "warn", children, testid, style }) {
  const cls = { ok: "k-tag-green", warn: "k-tag-warn", bad: "k-tag-red" }[kind];
  const Icon = STATUS_ICON[kind];
  return (
    <span className={cls} data-testid={testid} style={{ display: "inline-flex", alignItems: "center", gap: 6, ...style }}>
      <Icon size={14} aria-hidden /> {children}
    </span>
  );
}

function Card({ title, children, testid, help, handle }) {
  return (
    <div className="k-container" data-testid={testid} style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>{title}</h3>
        {help && <HelpTip text={help} testid={`${testid}-help`} />}
        {handle && <span style={{ marginLeft: "auto" }}>{handle}</span>}
      </div>
      {children}
    </div>
  );
}

export default function Admin() {
  const confirm = useConfirm();
  const { demo, setDemo } = useDemo();
  const [checklist, setChecklist] = useState(null);
  const [recon, setRecon] = useState(null);
  const [queue, setQueue] = useState(null);
  const [audit, setAudit] = useState(null);
  const [abuse, setAbuse] = useState([]);
  const [margin, setMargin] = useState(null);
  const [hostingChecklist, setHostingChecklist] = useState(null);
  const [grants, setGrants] = useState([]);
  const [grantForm, setGrantForm] = useState({ scope: "prompt_vault", target_workspace_id: "", reason: "", minutes: 30 });
  const [pricing, setPricing] = useState(null);
  const [pricingSave, setPricingSave] = useState("idle");
  const [beco, setBeco] = useState(null);
  const [auditParams, setAuditParams] = useState({ actor: "", action: "", resource_type: "", q: "", date_from: "", date_to: "", limit: 200 });
  const [frPhrase, setFrPhrase] = useState("");
  const [frPassword, setFrPassword] = useState("");
  const [frBusy, setFrBusy] = useState(false);
  const [integrity, setIntegrity] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);

  const fetchAudit = (p) => {
    api.get("/admin/audit-log", { params: p || auditParams }).then(r => setAudit(r.data)).catch(() => {});
  };

  const load = () => {
    api.get("/admin/launch-checklist").then(r => setChecklist(r.data)).catch(e => toast.error(errMsg(e)));
    api.get("/admin/ledger/reconcile").then(r => setRecon(r.data)).catch(() => {});
    api.get("/catalogue/verification-queue").then(r => setQueue(r.data)).catch(() => {});
    api.get("/admin/console/audit-integrity").then(r => setIntegrity(r.data)).catch(() => {});
    fetchAudit();
    api.get("/admin/abuse-queue").then(r => setAbuse(r.data)).catch(() => {});
    api.get("/admin/console/menu1").then(r => setMargin(r.data)).catch(() => {});
    api.get("/admin/hosting-launch-checklist").then(r => setHostingChecklist(r.data)).catch(() => {});
    api.get("/admin/console/privileged-grants").then(r => setGrants(r.data || [])).catch(() => {});
    api.get("/admin/pricing-config").then(r => setPricing(r.data)).catch(() => {});
    api.get("/coach/beco/analytics").then(r => setBeco(r.data)).catch(() => {});
  };
  useEffect(load, []);

  // Me15 — optimistic toggle with visible rollback on genuine failure; Me5 — a "saved" signal.
  const setPricingFlag = (key, val) => {
    const prev = pricing?.[key];
    setPricingSave("saving");
    optimistic({
      apply: () => setPricing(p => ({ ...p, [key]: val })),
      rollback: () => setPricing(p => ({ ...p, [key]: prev })),
      request: () => api.put("/admin/pricing-config", { [key]: val }),
      errorMessage: `Couldn't change ${key.replace(/_/g, " ")} — reverted.`,
    }).then((r) => { if (r) { setPricingSave("saved"); setTimeout(() => setPricingSave("idle"), 2500); } else setPricingSave("idle"); });
  };

  const markReviewed = async (slug) => {
    try { await api.post(`/admin/legal/${slug}/review`, {}); toast.success(`${slug} reviewed`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const verifyEntry = async (id) => {
    if (demo) { toast.info("Sample data — verifying is disabled in sample mode."); return; }
    try { await api.post(`/catalogue/${id}/verify`); toast.success(`${id} verified`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const createGrant = async () => {
    try { await api.post("/admin/console/privileged-grant", grantForm); toast.success("Grant issued"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revokeGrant = async (id) => {
    const ok = await confirm({ title: "Revoke this grant?", body: "The holder loses this privileged access immediately. This is logged.", confirmLabel: "Revoke" });
    if (!ok) return;
    try { await api.post(`/admin/console/privileged-grant/${id}/revoke`, {}); toast.success("Grant revoked"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doFactoryReset = async () => {
    const ok = await confirm({
      title: "Wipe ALL data to factory settings?",
      body: "This permanently deletes every user, organisation, project, ledger entry, audit record and all demo data. Only your Super Admin account is kept so you stay logged in. Catalogues, pricing and rules are re-seeded. This cannot be undone.",
      confirmLabel: "Wipe everything",
    });
    if (!ok) return;
    setFrBusy(true);
    try {
      const r = await api.post("/admin/console/factory-reset", { confirm_phrase: frPhrase, password: frPassword });
      toast.success(r.data?.message || "Platform reset to factory settings.");
      setFrPhrase(""); setFrPassword("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setFrBusy(false); }
  };

  const seedDemoData = async () => {
    const ok = await confirm({
      title: "Seed demo data?",
      body: "Adds a representative set of demo founders, orgs, projects and telemetry so the dashboards light up. Any previously seeded demo data is refreshed first. Real data is untouched.",
      confirmLabel: "Seed demo data",
    });
    if (!ok) return;
    setSeedBusy(true);
    try {
      const r = await api.post("/admin/console/seed-demo-data", {});
      toast.success(r.data?.message || "Demo data seeded.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSeedBusy(false); }
  };

  const q = demo ? DEMO.verifyQueue : queue;

  // ---- Me11: each console card is a reorderable widget on the admin home. ----
  const cards = [
    { key: "copilot", node: (h) => (
      <Card title="Kadana Copilot" testid="copilot-card" handle={h}
        help="An in-context assistant for platform, integration and local-compliance mechanics. It never gives legal, tax or regulatory advice; every message is rate-limited and metered.">
        <p className="k-muted" style={{ fontSize: 13, marginTop: 0 }}>In-context AI co-pilot for platform, integration and local-compliance mechanics. It explains what Kadana's tools do and never gives legal, tax or regulatory advice.</p>
        <KadanaCopilot />
      </Card>
    ) },
    pricing && { key: "pricing", node: (h) => (
      <Card title="Pricing visibility" testid="pricing-visibility-card" handle={h}
        help="Controls whether paid plans and prices are shown publicly. OFF returns the Pricing page to a waitlist state.">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <p className="k-muted" style={{ fontSize: 13, margin: 0 }}>Turn both ON to reveal Pro/Agency and the top-up tiers.</p>
          <span style={{ marginLeft: "auto" }}><SavedIndicator status={pricingSave} /></span>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="k-btn k-btn-ghost" data-testid="toggle-pricing-public" onClick={() => setPricingFlag("pricing_is_public", !pricing.pricing_is_public)}>
            Prices public: <strong style={{ marginLeft: 6, color: "var(--heading)" }}>{pricing.pricing_is_public ? "ON" : "OFF"}</strong>
          </button>
          <button className="k-btn k-btn-ghost" data-testid="toggle-plans-public" onClick={() => setPricingFlag("plans_public", !pricing.plans_public)}>
            Paid plans public: <strong style={{ marginLeft: 6, color: "var(--heading)" }}>{pricing.plans_public ? "ON" : "OFF"}</strong>
          </button>
        </div>
      </Card>
    ) },
    { key: "checklist", node: (h) => (
      <Card title="Launch checklist" testid="launch-checklist" handle={h}
        help="Public site can't go live until every legal/regulatory page is marked reviewed by an admin.">
        {checklist?.banner && <div className="k-badge k-badge-sandbox" style={{ marginBottom: 12 }}>{checklist.banner}</div>}
        <p className="k-muted" style={{ fontSize: 13 }}>{checklist?.note}</p>
        <table className="k-table"><tbody>
          {checklist && Object.entries(checklist.pages).map(([slug, status]) => (
            <tr key={slug}>
              <td className="k-heading">{slug}</td>
              <td><StatusTag kind={status.startsWith("Reviewed") ? "ok" : "warn"}>{status}</StatusTag></td>
              <td>{!status.startsWith("Reviewed") && <button className="k-btn k-btn-ghost" data-testid={`review-${slug}`} onClick={() => markReviewed(slug)}>Mark reviewed</button>}</td>
            </tr>
          ))}
        </tbody></table>
        <StatusTag kind={checklist?.can_go_live ? "ok" : "bad"} style={{ marginTop: 10, fontWeight: 600 }}>Can go live: {String(checklist?.can_go_live)}</StatusTag>
      </Card>
    ) },
    { key: "recon", node: (h) => (
      <Card title="Ledger reconciliation" testid="ledger-recon" handle={h}
        help="Confirms the credit ledger balances across all orgs and flags any negative balances.">
        <StatusTag kind={recon?.reconciles ? "ok" : "bad"} style={{ fontWeight: 600 }}>Reconciles: {String(recon?.reconciles)} · {recon?.orgs} orgs</StatusTag>
        {recon?.negative_balances?.length > 0 && <StatusTag kind="bad" style={{ marginLeft: 8 }}>Negative balances detected!</StatusTag>}
      </Card>
    ) },
    { key: "verify", node: (h) => (
      <Card title="Connector verification queue" testid="verify-queue" handle={h}
        help="Connectors whose access route isn't yet verified. Verifying lets a region be marketed as verified. Sort, filter and export the priority list below.">
        <p className="k-muted" style={{ fontSize: 13 }}>{fmt.plural(q?.unverified_total ?? 0, "connector")} unverified. {q?.note}</p>
        <DataTable testid="verify-table" csvName="verification-queue.csv"
          columns={[
            { key: "id", label: "ID" },
            { key: "name", label: "Name" },
            { key: "access_class", label: "Class" },
            { key: "action", label: "", sortable: false, hideable: false, csv: () => "",
              render: (e) => <button className="k-btn k-btn-ghost" data-testid={`verify-${e.id}`} onClick={() => verifyEntry(e.id)} style={{ padding: "6px 12px", fontSize: 13 }}>Verify</button> },
          ]}
          rows={q?.priority || []} emptyTitle="No connectors awaiting verification." />
      </Card>
    ) },
    { key: "abuse", node: (h) => (
      <Card title="Abuse & dispute queue" testid="abuse-queue" handle={h} help="Reports and disputes awaiting a human decision.">
        {abuse.length === 0 ? <p className="k-muted">Empty.</p> : abuse.map(a => <div key={a.id} className="k-muted" style={{ fontSize: 13 }}>{a.type} · {a.status} · {String(a.at).slice(0, 19)}</div>)}
      </Card>
    ) },
    { key: "audit", node: (h) => {
      const setAP = (k, v) => setAuditParams(p => ({ ...p, [k]: v }));
      return (
      <Card title="Audit log — every transaction (hash-chained)" testid="audit-log" handle={h}
        help="Append-only, hash-chained record of every state-changing request and every sensitive read, by any admin or user. 'Chain intact' means no entry was altered or removed. Filter by who/what/when and export to CSV.">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <StatusTag kind={audit?.chain_intact ? "ok" : "bad"} style={{ fontWeight: 600 }}>Chain intact: {String(audit?.chain_intact)}</StatusTag>
          <span className="k-muted" style={{ fontSize: 12 }}>{fmt.plural(audit?.total ?? 0, "entry", "entries")} total · showing {audit?.entries?.length ?? 0}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <input className="k-control" data-testid="audit-filter-actor" placeholder="actor (email/id)" style={{ maxWidth: 180 }} value={auditParams.actor} onChange={e => setAP("actor", e.target.value)} />
          <input className="k-control" data-testid="audit-filter-action" placeholder="action (e.g. POST /api/...)" style={{ maxWidth: 210 }} value={auditParams.action} onChange={e => setAP("action", e.target.value)} />
          <select className="k-control" data-testid="audit-filter-resource" style={{ maxWidth: 180 }} value={auditParams.resource_type} onChange={e => setAP("resource_type", e.target.value)}>
            <option value="">all resource types</option>
            {(audit?.resource_types || []).map(rt => <option key={rt} value={rt}>{rt}</option>)}
          </select>
          <input className="k-control" data-testid="audit-filter-from" type="date" style={{ maxWidth: 160 }} value={auditParams.date_from} onChange={e => setAP("date_from", e.target.value)} />
          <input className="k-control" data-testid="audit-filter-to" type="date" style={{ maxWidth: 160 }} value={auditParams.date_to} onChange={e => setAP("date_to", e.target.value)} />
          <button className="k-btn k-btn-primary" data-testid="audit-apply" onClick={() => fetchAudit()}>Apply</button>
          <button className="k-btn k-btn-ghost" data-testid="audit-clear" onClick={() => { const p = { actor: "", action: "", resource_type: "", q: "", date_from: "", date_to: "", limit: 200 }; setAuditParams(p); fetchAudit(p); }}>Clear</button>
        </div>
        <DataTable testid="audit-table" csvName="audit-log.csv"
          columns={[
            { key: "seq", label: "#", render: (e) => <span className="mono">{e.seq}</span> },
            { key: "at", label: "When", csv: (e) => e.at, render: (e) => <RelTime value={e.at} /> },
            { key: "actor", label: "Actor" },
            { key: "action", label: "Action", render: (e) => <span className="mono" style={{ fontSize: 11 }}>{e.action}</span> },
            { key: "resource_type", label: "Resource" },
            { key: "resource_id", label: "Resource ID", render: (e) => <span className="mono" style={{ fontSize: 11 }}>{e.resource_id || "—"}</span> },
            { key: "status", label: "Status", csv: (e) => e.details?.status ?? "", render: (e) => e.details?.status ?? "—" },
          ]}
          rows={audit?.entries || []} emptyTitle="No audit entries match these filters." />
      </Card>
      );
    } },
    { key: "margin", node: (h) => (
      <Card title="Margin & token burn" testid="margin-panel" handle={h}
        help="AI/generation and blended margins against the protected floor. An icon (not colour alone) marks above/below floor.">
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div>
            <div className="k-muted" style={{ fontSize: 12 }}>AI / generation margin</div>
            <div className="k-heading" data-testid="ai-margin" style={{ fontSize: 26, display: "flex", alignItems: "center", gap: 8 }}>
              {margin != null && margin.ai_generation_margin_pct != null && (margin.ai_generation_margin_pct >= (margin?.margin_floor_pct ?? 60)
                ? <CheckCircle2 size={20} className="k-tag-green" data-testid="ai-margin-icon" aria-label="above floor" />
                : <AlertTriangle size={20} className="k-tag-warn" data-testid="ai-margin-icon" aria-label="below floor" />)}
              {margin?.ai_generation_margin_pct ?? "—"}%
            </div>
          </div>
          <div>
            <div className="k-muted" style={{ fontSize: 12 }}>Blended margin (vs {margin?.margin_floor_pct ?? 60}% floor)</div>
            <div className="k-heading" data-testid="blended-margin" style={{ fontSize: 26, display: "flex", alignItems: "center", gap: 8 }}>
              {margin != null && margin.blended_margin_pct != null && (margin.blended_margin_pct >= (margin?.margin_floor_pct ?? 60)
                ? <CheckCircle2 size={20} className="k-tag-green" data-testid="blended-margin-icon" aria-label="above floor" />
                : <AlertTriangle size={20} className="k-tag-warn" data-testid="blended-margin-icon" aria-label="below floor" />)}
              {margin?.blended_margin_pct ?? "—"}%
            </div>
          </div>
        </div>
        <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>{margin?.blended_margin_label}</p>
      </Card>
    ) },
    { key: "hosting", node: (h) => (
      <Card title="Hosting launch gate" testid="hosting-launch-checklist" handle={h} help="Managed-hosting can't go live until each item here is reviewed.">
        <StatusTag kind={hostingChecklist?.hosting_can_go_live ? "ok" : "bad"} style={{ fontWeight: 600, marginBottom: 8 }}>Hosting can go live: {String(hostingChecklist?.hosting_can_go_live)}</StatusTag>
        <table className="k-table"><tbody>
          {hostingChecklist && Object.entries(hostingChecklist.pages).map(([slug, status]) => (
            <tr key={slug}>
              <td className="k-heading">{slug}</td>
              <td><StatusTag kind={String(status).startsWith("Reviewed") ? "ok" : "warn"}>{status}</StatusTag></td>
              <td>{!String(status).startsWith("Reviewed") && <button className="k-btn k-btn-ghost" data-testid={`review-${slug}`} onClick={() => markReviewed(slug)}>Mark reviewed</button>}</td>
            </tr>
          ))}
        </tbody></table>
      </Card>
    ) },
    { key: "grants", node: (h) => (
      <Card title="Privileged access grants" testid="privileged-grants" handle={h}
        help="Reason-required, time-boxed, audit-logged. View prompts, impersonate and container terminal all require an active grant.">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <select className="k-control" data-testid="grant-scope" style={{ maxWidth: 180 }} value={grantForm.scope} onChange={e => setGrantForm({ ...grantForm, scope: e.target.value })}>
            <option value="prompt_vault">view prompts</option>
            <option value="impersonation">impersonate user</option>
            <option value="terminal">container terminal</option>
          </select>
          <input className="k-control" data-testid="grant-workspace" placeholder="workspace/org id" style={{ maxWidth: 200 }} value={grantForm.target_workspace_id} onChange={e => setGrantForm({ ...grantForm, target_workspace_id: e.target.value })} />
          <input className="k-control" data-testid="grant-reason" placeholder="reason (required)" style={{ flex: 1, minWidth: 180 }} value={grantForm.reason} onChange={e => setGrantForm({ ...grantForm, reason: e.target.value })} />
          <input className="k-control" data-testid="grant-minutes" type="number" placeholder="minutes" style={{ maxWidth: 110 }} value={grantForm.minutes} onChange={e => setGrantForm({ ...grantForm, minutes: Number(e.target.value) })} />
          <button className="k-btn k-btn-primary" data-testid="grant-create" onClick={createGrant}>Issue grant</button>
        </div>
        <DataTable testid="grants-table" csvName="privileged-grants.csv"
          columns={[
            { key: "action", label: "Action", csv: (g) => g.action || g.scope, render: (g) => g.action || g.scope },
            { key: "target_workspace_id", label: "Workspace" },
            { key: "status", label: "Status", render: (g) => <StatusTag kind={g.status === "active" ? "ok" : "warn"}>{g.status}</StatusTag> },
            { key: "expires_at", label: "Expires", render: (g) => <span className="mono" style={{ fontSize: 11 }}>{String(g.expires_at).slice(0, 19)}</span> },
            { key: "revoke", label: "", sortable: false, hideable: false, csv: () => "",
              render: (g) => g.status === "active" ? <button className="k-btn k-btn-ghost" data-testid={`revoke-${g.id}`} onClick={() => revokeGrant(g.id)} style={{ padding: "6px 12px", fontSize: 13 }}>Revoke</button> : null },
          ]}
          rows={grants} emptyTitle="No privileged grants issued." />
      </Card>
    ) },
    { key: "beco", node: (h) => (
      <Card title="BeCo sub-metric analytics" testid="beco-analytics" handle={h}
        help="Which of the ten behavioural sub-metrics correlate most with completed, sandbox-verified builds versus abandoned ones, across the user base. A product-improvement signal — not a vanity dashboard, and never shown next to CRX.">
        <p className="k-muted" style={{ fontSize: 13, marginTop: 0 }}>{beco?.note} Sample: {beco?.sample_users ?? 0} users.</p>
        <DataTable testid="beco-analytics-table" csvName="beco-submetric-analytics.csv"
          columns={[
            { key: "label", label: "Sub-metric" },
            { key: "family", label: "Family" },
            { key: "correlation_with_completion", label: "r (vs completion)",
              render: (c) => c.correlation_with_completion == null ? <span className="k-muted">insufficient data</span> : c.correlation_with_completion },
            { key: "sample", label: "n" },
          ]}
          rows={beco?.correlations || []} emptyTitle="No BeCo telemetry yet — the engine populates as builds accrue." />
      </Card>
    ) },
    { key: "factory-reset", node: (h) => (
      <Card title="Factory reset — kill switch" testid="factory-reset" handle={h}
        help="Wipes ALL platform and user data and reverts to factory settings, keeping only your Super Admin account. Use it to clear demo data. This cannot be undone.">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <ShieldAlert size={16} className="k-tag-red" aria-hidden />
          <p className="k-muted" style={{ fontSize: 13, margin: 0 }}>
            Permanently deletes every user, organisation, project, ledger, audit record and all demo data.
            Only your Super Admin account is kept; catalogues, pricing and rules are re-seeded.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input className="k-control" data-testid="factory-reset-phrase" placeholder='Type FACTORY RESET' style={{ maxWidth: 220 }} value={frPhrase} onChange={e => setFrPhrase(e.target.value)} />
          <input className="k-control" data-testid="factory-reset-password" type="password" placeholder="Your Super Admin password" style={{ maxWidth: 240 }} value={frPassword} onChange={e => setFrPassword(e.target.value)} />
          <button className="k-btn k-btn-danger" data-testid="factory-reset-btn" disabled={frBusy || frPhrase !== "FACTORY RESET" || !frPassword}
            onClick={doFactoryReset} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RotateCcw size={14} /> {frBusy ? "Wiping…" : "Wipe to factory settings"}
          </button>
        </div>
        <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>The button unlocks only once you type the exact phrase and enter your password. The reset itself is written as a fresh genesis entry in the audit log.</p>
      </Card>
    ) },
  ].filter(Boolean);

  const { ordered, move } = useWidgetOrder("kadana_admin_widgets", cards);

  return (
    <Shell active="admin">
      <div className="k-admin-dense">
      <div className="k-h-accent" style={{ marginBottom: 6 }}>
        <h1 className="k-heading" style={{ fontSize: 30, marginTop: 0, marginBottom: 2 }}>Admin console</h1>
        <Greeting testid="admin-greeting" />
      </div>
      {integrity && !integrity.intact && (
        <div data-testid="audit-tamper-alert" role="alert" className="k-container" style={{ padding: 16, marginTop: 12, border: "1px solid var(--danger, #ef4444)", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <ShieldAlert size={20} className="k-tag-red" aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div className="k-heading" style={{ fontSize: 15, marginBottom: 2 }}>Audit log tampering detected</div>
            <p className="k-muted" style={{ fontSize: 13, margin: 0 }}>
              The hash-chained audit log failed integrity verification — {integrity.broken_count} of {integrity.checked_entries} entries are broken, first at <span className="mono">#{integrity.first_broken_seq}</span>. Investigate immediately; an alert email has been sent to the Super Admin address.
            </p>
          </div>
        </div>
      )}
      <OrientationTour tourKey="admin_v1" steps={ADMIN_TOUR} testid="admin-orientation" />
      <PresenceBar scope="admin-console" testid="admin-presence" />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", margin: "12px 0 18px" }}>
        <a className="k-btn k-btn-primary" data-testid="open-command-center" href="/admin/command-center" style={{ display: "inline-flex" }}>Command Center →</a>
        <a className="k-btn k-btn-ghost" data-testid="open-governance-console" href="/admin/governance" style={{ display: "inline-flex" }}>Menu 11 — Catalogue & Data Governance →</a>
        <button className="k-btn k-btn-ghost" data-testid="seed-demo-data" onClick={seedDemoData} disabled={seedBusy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FlaskConical size={14} /> {seedBusy ? "Seeding…" : "Seed demo data"}
        </button>
        <button className="k-btn k-btn-ghost" data-testid="toggle-demo-mode" onClick={() => setDemo(!demo)} style={{ marginLeft: "auto" }}>
          <FlaskConical size={14} /> Sample data: <strong style={{ marginLeft: 6, color: "var(--heading)" }}>{demo ? "ON" : "OFF"}</strong>
        </button>
      </div>

      {ordered.map((c, i) => (
        <div key={c.key}>
          {c.node(
            <ReorderHandle testid={`admin-reorder-${c.key}`} isFirst={i === 0} isLast={i === ordered.length - 1}
              onUp={() => move(c.key, -1)} onDown={() => move(c.key, 1)} />
          )}
        </div>
      ))}
      </div>
    </Shell>
  );
}
